import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

import { TierDto } from '@common/data-services/fbo-monolith-commission-program-data/models/commission-program.dto';
import { ConfigurationService } from '@configuration/service/configuration.service';

import { ExpenseInvoiceTabFormService } from './expense-invoice-tab-form.service';
import { TimeMaterialInvoiceTabFormService } from './time-material-invoice-tab-form.service';
import { IFormService, PhxConstants } from '../../../common/model';
import { FormArray, FormBuilder, FormControl, FormGroup } from '../../../common/ngx-strongly-typed-forms';
import { IBillingInfo, IBillingInvoice, ICommissionRate, IJobOwner, ITabCoreCommissions, IWorkOrder, IWorkOrderVersionCommission } from '../../models';
import { PtFieldViewCustomValidator } from '../../ptFieldCustomValidator';
import { WorkOrderGrossMarginService } from '../work-order-gross-margin.service';

@Injectable()
export class CoreCommissionFormService implements IFormService {
  isBusinessRulesEnabled = false;
  formGroup: FormGroup<ITabCoreCommissions>;

  commissionsIsHidden = false;
  showCommissionRateColumn = false;

  private isRootComponentDestroyed$: Subject<boolean>;

  workOrder: IWorkOrder;

  constructor(
    private readonly fb: FormBuilder,
    private readonly expenseInvoiceTabFormService: ExpenseInvoiceTabFormService,
    private readonly timeMaterialInvoiceTabFormService: TimeMaterialInvoiceTabFormService,
    private readonly configurationService: ConfigurationService,
    private readonly workOrderGrossMarginService: WorkOrderGrossMarginService
  ) {
  }

  get usesSupportFormControl(): FormControl<boolean> {
    return this.formGroup.get('JobOwnerUsesSupport') as FormControl<boolean>;
  }

  get salePatternIdFormControl(): FormControl<number> {
    return this.formGroup.get('SalesPatternId') as FormControl<number>;
  }

  get jobOwnerFormGroup(): FormGroup<IJobOwner> {
    return this.formGroup.get('JobOwner') as FormGroup<IJobOwner>;
  }

  get recruitersFormArray(): FormArray<IJobOwner> {
    return this.formGroup.get('Recruiters') as FormArray<IJobOwner>;
  }

  get commissionsFormArray(): FormArray<IJobOwner> {
    return this.formGroup.get('WorkOrderVersionCommissions') as FormArray<IJobOwner>;
  }

  get supportingJobOwnerFormArray(): FormArray<IJobOwner> {
    return this.formGroup.get('SupportingJobOwners') as FormArray<IJobOwner>;
  }

  get isRecruiterRemovedFormControl(): FormControl<boolean> {
    return this.formGroup.get('IsRecruiterRemoved') as FormControl<boolean>;
  }

  get usesSupportChange$() {
    return this.usesSupportFormControl.valueChanges;
  }

  get salePatternIdChange$() {
    return this.salePatternIdFormControl.valueChanges;
  }

  get jobOwnerChange$() {
    return this.jobOwnerFormGroup.valueChanges;
  }

  get userProfileIdSaleChange$() {
    return this.jobOwnerFormGroup.get('UserProfileIdSales').valueChanges;
  }

  createForm(workorder: IWorkOrder, isDestroyed$: Subject<boolean>) {
    this.isRootComponentDestroyed$ = isDestroyed$;

    this.workOrder = workorder;

    /** NOTE: if the 'HideCommissions' feature is active then exclude validation and listeners */
    this.configurationService.isFeatureActive$([PhxConstants.FeatureFlags.HideCommissions]).pipe(
      takeUntil(this.isRootComponentDestroyed$)
    ).subscribe(featureFlagState => {
      this.commissionsIsHidden = featureFlagState[PhxConstants.FeatureFlags.HideCommissions];
    });

    this.isBusinessRulesEnabled = workorder.readOnlyStorage.IsCommissionsEditable;

    const commission: ITabCoreCommissions = this.mapWorkOrderToFormData(workorder);
    
    this.setShowCommissionRateColumn(commission.WorkOrderVersionCommissions);

    const validateComplianceDraftAndNewWO = workorder.StatusId === PhxConstants.WorkOrderStatus.Processing || workorder.WorkOrderVersion.ValidateComplianceDraft;

    this.formGroup = this.fb.group<ITabCoreCommissions>({
      SalesPatternId: [commission.SalesPatternId],
      JobOwnerUsesSupport: [
        commission.JobOwnerUsesSupport,
        (!this.commissionsIsHidden && !commission.SalesPatternId && validateComplianceDraftAndNewWO)
          ? PtFieldViewCustomValidator.checkPtFieldViewCustomValidator('WorkOrderVersion', 'JobOwnerUsesSupport', [
            Validators.required
          ])
          : null
      ],
      JobOwner: this.createJobOwnerFormGroup(commission.JobOwner, false,
        'WorkOrderVersion.JobOwner', validateComplianceDraftAndNewWO),
      SupportingJobOwners: this.createJobOwnerFormArray(commission.SupportingJobOwners, false,
        'WorkOrderVersion.SupportingJobOwners', false, validateComplianceDraftAndNewWO),
      Recruiters: this.createJobOwnerFormArray(commission.Recruiters, false,
        'WorkOrderVersion.Recruiters', false, validateComplianceDraftAndNewWO),
      IsRecruiterRemoved: [commission.IsRecruiterRemoved],
      WorkOrderVersionCommissions: this.createJobOwnerFormArray(commission.WorkOrderVersionCommissions, true,
        'WorkOrderVersion.WorkOrderVersionCommissions', false, validateComplianceDraftAndNewWO)
    });

    this.formGroup.controls.WorkOrderVersionCommissions.setValidators(this.maxTotalValidator('MarginSplitPercentage', 100));

    return this.formGroup;
  }

  destroyForm() {
    this.formGroup = null;
  }

  setupFormListeners() {
    this.isRootComponentDestroyed$.pipe(takeUntil(this.isRootComponentDestroyed$)).subscribe(() => {
      this.destroyForm();
    });
    if (!this.commissionsIsHidden) {
      this.observeSalesPatternChange();
      this.observeJobOwnerFormGroup();
    }
  }

  updateUserProfileIdSale(value: number, emitEvent = false) {
    this.jobOwnerFormGroup.get('UserProfileIdSales').setValue(value, { emitEvent });
  }

  resetSupportingJobOwner() {
    this.clearArray(this.supportingJobOwnerFormArray);
  }

  formGroupToPartial(workOrder: IWorkOrder): IWorkOrder {
    /** NOTE: if core tab commissions section is hidden just return original WO */
    if (!this.commissionsIsHidden) {
      const commissionDetails: ITabCoreCommissions = this.formGroup.value;

      const wovCommissions = commissionDetails.WorkOrderVersionCommissions as IWorkOrderVersionCommission[];

      /** NOTE: user selections can make it possible to have null CommissionRoleId and UserProfileIdSales  */
      workOrder.WorkOrderVersion.WorkOrderVersionCommissions = wovCommissions?.filter(f => !!f.CommissionRoleId && !!f.UserProfileIdSales);
      workOrder.WorkOrderVersion.SalesPatternId = commissionDetails.SalesPatternId;
      workOrder.WorkOrderVersion.JobOwnerUsesSupport = commissionDetails.JobOwnerUsesSupport;
      workOrder.WorkOrderVersion.JobOwner = commissionDetails.JobOwner;
      workOrder.WorkOrderVersion.Recruiters = commissionDetails.Recruiters;
      workOrder.WorkOrderVersion.IsRecruiterRemoved = commissionDetails.IsRecruiterRemoved;
      workOrder.WorkOrderVersion.SupportingJobOwners = commissionDetails.SupportingJobOwners;
    }

    return workOrder;
  }

  updateForm(workorder: IWorkOrder) {
    this.isBusinessRulesEnabled = workorder.readOnlyStorage.IsCommissionsEditable;

    this.workOrder = workorder;

    const commission: ITabCoreCommissions = this.mapWorkOrderToFormData(workorder);

    const validateComplianceDraftAndNewWO = workorder.StatusId === PhxConstants.WorkOrderStatus.Processing || workorder.WorkOrderVersion.ValidateComplianceDraft;

    if (!this.commissionsIsHidden && !commission.SalesPatternId && validateComplianceDraftAndNewWO) {
      this.usesSupportFormControl.setValidators(
        PtFieldViewCustomValidator.checkPtFieldViewCustomValidator('WorkOrderVersion', 'JobOwnerUsesSupport', [
          Validators.required
        ])
      );
    } else {
      this.usesSupportFormControl.setValidators(null);
    }

    this.formGroup.patchValue({
      SalesPatternId: commission.SalesPatternId,
      IsRecruiterRemoved: commission.IsRecruiterRemoved,
      JobOwnerUsesSupport: commission.JobOwnerUsesSupport
    }, { emitEvent: false });

    this.updateJobOwnerFormGroup(this.jobOwnerFormGroup, commission.JobOwner, false,
      'WorkOrderVersion.JobOwner', validateComplianceDraftAndNewWO);

    this.updateJobOwnerFormArray(this.supportingJobOwnerFormArray, commission.SupportingJobOwners, false,
      'WorkOrderVersion.SupportingJobOwners', false, validateComplianceDraftAndNewWO);

    this.updateJobOwnerFormArray(this.recruitersFormArray, commission.Recruiters, false,
      'WorkOrderVersion.Recruiters', false, validateComplianceDraftAndNewWO);

    this.updateVersionCommissionFormArray(commission.WorkOrderVersionCommissions, validateComplianceDraftAndNewWO);
  }

  updateVersionCommissionFormArray(commissions: Array<IJobOwner>, validate?: boolean) {
    validate ??= this.workOrder.StatusId === PhxConstants.WorkOrderStatus.Processing || this.workOrder.WorkOrderVersion.ValidateComplianceDraft;

    this.updateJobOwnerFormArray(this.commissionsFormArray, commissions, true,
      'WorkOrderVersion.WorkOrderVersionCommissions', false, validate);

    this.setShowCommissionRateColumn(commissions);
  }

  resetRecruiterFormArray() {
    this.clearArray(this.recruitersFormArray);
  }

  createJobOwnerFormArray(
    jobOwners: Partial<IJobOwner>[],
    isCommissionRateHeaderValidation: boolean = false,
    modelPrefix?: string,
    requireNonEmpty: boolean = false,
    validateComplianceDraftAndNewWO?: boolean
  ): FormArray<IJobOwner> {
    const arr = jobOwners?.length || !requireNonEmpty ? jobOwners : new Array<Partial<IJobOwner>>({});
    return this.fb.array<IJobOwner>(
      arr.map((support: any) =>
        this.createJobOwnerFormGroup(support, isCommissionRateHeaderValidation, modelPrefix, validateComplianceDraftAndNewWO)
      )
    );
  }

  createJobOwnerFormGroup(
    jobOwner: Partial<IJobOwner> = {},
    isCommissionRateHeaderValidation: boolean = false,
    modelPrefix?: string,
    validateComplianceDraftAndNewWO?: boolean
  ): FormGroup<IJobOwner> {
    const commissionRateResult = this.getCommissionRatePercentage(jobOwner.CommissionRateHeaderId, jobOwner.IsOverridden, jobOwner.CommissionRatePercentage, jobOwner.CommissionRates);

    return this.fb.group<IJobOwner>({
      Id: [jobOwner.Id ? jobOwner.Id : 0],
      UserProfileIdSales: [
        jobOwner.UserProfileIdSales,
        (!this.commissionsIsHidden && ((isCommissionRateHeaderValidation && jobOwner.IsApplicable) || !isCommissionRateHeaderValidation && validateComplianceDraftAndNewWO))
          ? PtFieldViewCustomValidator.checkPtFieldViewCustomValidator(modelPrefix, 'UserProfileIdSales', [
            Validators.required
          ])
          : null
      ],
      CommissionRoleId: [jobOwner.CommissionRoleId],
      IsApplicable: [jobOwner.IsApplicable],
      CommissionRateHeaderId: [
        jobOwner.CommissionRateHeaderId,
        !this.commissionsIsHidden && isCommissionRateHeaderValidation && jobOwner.IsApplicable && validateComplianceDraftAndNewWO
          ? PtFieldViewCustomValidator.checkPtFieldViewCustomValidator(modelPrefix, 'UserProfileIdSales', [
            Validators.required
          ])
          : null
      ],
      FullName: [jobOwner.FullName],
      Description: [jobOwner.Description],
      CommissionRates: this.createCommissionRateFormArray(jobOwner.CommissionRates, jobOwner.CommissionRateHeaderId),
      IsHouseAccount: [jobOwner.IsHouseAccount],      
      DisplayCommissionRatePercentage: [commissionRateResult?.ratePercentage],
      CommissionRatePercentage: [commissionRateResult?.isCommissionRateProgram ? commissionRateResult.ratePercentage : null],
      IsOverridden: [jobOwner.IsOverridden ?? false],
      OverrideReason: [jobOwner.OverrideReason],
      OverrideByUserProfileId: [jobOwner.OverrideByUserProfileId],
      OverrideByFullName: [jobOwner.OverrideByFullName],
      OverrideDatetime: [jobOwner.OverrideDatetime],
      MarginSplitPercentage: [jobOwner.MarginSplitPercentage]
    });
  }

  createBlankPartialJobOwnerFormGroup(): FormGroup<Partial<IJobOwner>> {
    return this.fb.group<Partial<IJobOwner>>({
      Id: [0],
      UserProfileIdSales: [null, [Validators.required]],
      CommissionRoleId: [null],
      IsApplicable: [null],
      CommissionRateHeaderId: [null],
      FullName: [null],
      Description: [null],
      CommissionRates: this.fb.array([]),
      IsHouseAccount: [null],
      DisplayCommissionRatePercentage: [null],
      CommissionRatePercentage: [null],
      IsOverridden: [false],
      OverrideReason: [null],
      MarginSplitPercentage: [null]
    });
  }

  checkRecruiters(isRecruitersAllowed: boolean) {
    const recruiters = this.recruitersFormArray.value;
    const isRecruiterRemoved = this.isRecruiterRemovedFormControl.value;
    if (recruiters.length === 0 && isRecruitersAllowed && !isRecruiterRemoved) {
      this.addRecruiter();
    }
  }

  addRecruiter() {
    const formArray = this.recruitersFormArray as FormArray<Partial<IJobOwner>>;
    formArray.push(this.createBlankPartialJobOwnerFormGroup());
    this.isRecruiterRemovedFormControl.patchValue(false, { emitEvent: false });
  }

  deleteRecruiter(index: number) {
    this.recruitersFormArray.removeAt(index);
    if (this.recruitersFormArray.length === 0) {
      this.isRecruiterRemovedFormControl.patchValue(true, { emitEvent: false });
    }
  }

  addSupportingJobOwner() {
    const formArray = this.supportingJobOwnerFormArray as FormArray<Partial<IJobOwner>>;
    formArray.push(this.createBlankPartialJobOwnerFormGroup());
  }

  deleteSupportingJobOwner(index: number) {
    this.supportingJobOwnerFormArray.removeAt(index);
  }

  private observeSalesPatternChange() {
    this.salePatternIdChange$.pipe(
      filter(() => this.isBusinessRulesEnabled),
      distinctUntilChanged(),
      takeUntil(this.isRootComponentDestroyed$)
    ).subscribe(value => {
      if (value) {
        this.usesSupportFormControl.setValidators(null);
      } else {
        let emitEvent = false;
        if (this.jobOwnerFormGroup.value) {
          this.jobOwnerFormGroup.get('UserProfileIdSales').setValue(null, { emitEvent: false });
          emitEvent = true;
        }
        this.usesSupportFormControl.setValue(null, { emitEvent });
        this.clearArray(this.supportingJobOwnerFormArray);
      }
    });
  }

  private observeJobOwnerFormGroup() {
    this.jobOwnerChange$.pipe(
      filter(() => this.isBusinessRulesEnabled),
      distinctUntilChanged(),
      takeUntil(this.isRootComponentDestroyed$)
    ).subscribe(value => {
      const jobOwner = value.UserProfileIdSales;
      this.setBillingReferenceContactProfileId(this.timeMaterialInvoiceTabFormService.billingInfoFormArray, jobOwner);
      this.setBillingReferenceContactProfileId(this.expenseInvoiceTabFormService.billingInfoFormArray, jobOwner);
    });
  }

  private setBillingReferenceContactProfileId(BillingInfoes: FormArray<IBillingInfo>, jobOwner: number) {
    const billInfoLength = BillingInfoes ? BillingInfoes.length : 0;
    for (let i = 0; i < billInfoLength; i++) {
      const BillingInvoices = BillingInfoes.at(i).get('BillingInvoices') as FormArray<IBillingInvoice>;
      const billInvoiceLength = BillingInvoices ? BillingInvoices.length : 0;
      for (let j = 0; j < billInvoiceLength; j++) {
        const BillingReferenceContactProfileId = BillingInvoices.at(j).get('BillingReferenceContactProfileId');
        if (BillingReferenceContactProfileId) {
          BillingReferenceContactProfileId.setValue(jobOwner, { emitEvent: false });
        }
      }
    }
  }

  private mapWorkOrderToFormData(workorder: IWorkOrder): ITabCoreCommissions {
    return {
      SalesPatternId: workorder.WorkOrderVersion.SalesPatternId,
      JobOwnerUsesSupport: workorder.WorkOrderVersion.JobOwnerUsesSupport,
      JobOwner: workorder.WorkOrderVersion.JobOwner,
      SupportingJobOwners: workorder.WorkOrderVersion.SupportingJobOwners,
      Recruiters: workorder.WorkOrderVersion.Recruiters,
      IsRecruiterRemoved: workorder.WorkOrderVersion.IsRecruiterRemoved,
      WorkOrderVersionCommissions: workorder.WorkOrderVersion.WorkOrderVersionCommissions
    };
  }

  private updateJobOwnerFormArray(
    formArray: FormArray<IJobOwner>,
    items: Partial<IJobOwner>[],
    isCommissionRateHeaderValidation: boolean = false,
    modelPrefix?: string,
    requireNonEmpty: boolean = false,
    validateComplianceDraftAndNewWO?: boolean
  ) {
    if (formArray.length && items.length) {
      items.forEach((item, index) => {
        const formGroup = formArray.at(index) as FormGroup<IJobOwner>;
        if (formGroup) {
          this.updateJobOwnerFormGroup(formGroup, item, isCommissionRateHeaderValidation, modelPrefix, validateComplianceDraftAndNewWO);
        } else {
          formArray.push(this.createJobOwnerFormGroup(item, isCommissionRateHeaderValidation, modelPrefix, validateComplianceDraftAndNewWO));
        }
      });
      if (formArray.length > items.length) {
        this.clearArray(formArray, items.length);
      }
    } else if (items.length) {
      const array = this.createJobOwnerFormArray(items, isCommissionRateHeaderValidation, modelPrefix, requireNonEmpty, validateComplianceDraftAndNewWO);
      array.controls.forEach(group => {
        formArray.push(group);
      });
    } else {
      this.clearArray(formArray);
    }
  }

  private updateJobOwnerFormGroup(
    formGroup: FormGroup<IJobOwner>,
    jobOwner: Partial<IJobOwner> = {},
    isCommissionRateHeaderValidation: boolean = false,
    modelPrefix?: string,
    validateComplianceDraftAndNewWO?: boolean) {

    if (!this.commissionsIsHidden && ((isCommissionRateHeaderValidation && jobOwner.IsApplicable) || !isCommissionRateHeaderValidation && validateComplianceDraftAndNewWO)) {
      formGroup.get('UserProfileIdSales').setValidators(
        PtFieldViewCustomValidator.checkPtFieldViewCustomValidator(modelPrefix, 'UserProfileIdSales', [
          Validators.required
        ])
      );
    } else {
      formGroup.get('UserProfileIdSales').setValidators(null);
    }

    if (!this.commissionsIsHidden && isCommissionRateHeaderValidation && jobOwner.IsApplicable && validateComplianceDraftAndNewWO) {
      formGroup.get('CommissionRateHeaderId').setValidators(
        PtFieldViewCustomValidator.checkPtFieldViewCustomValidator(modelPrefix, 'UserProfileIdSales', [
          Validators.required
        ])
      );
    } else {
      formGroup.get('CommissionRateHeaderId').setValidators(null);
    }

    const commissionRateResult = this.getCommissionRatePercentage(jobOwner.CommissionRateHeaderId, jobOwner.IsOverridden, jobOwner.CommissionRatePercentage, jobOwner.CommissionRates);

    formGroup.patchValue({
      Id: jobOwner.Id ? jobOwner.Id : 0,
      CommissionRoleId: jobOwner.CommissionRoleId,
      IsApplicable: jobOwner.IsApplicable,
      CommissionRateHeaderId: jobOwner.CommissionRateHeaderId,
      FullName: jobOwner.FullName,
      Description: jobOwner.Description,
      IsHouseAccount: jobOwner.IsHouseAccount,
      UserProfileIdSales: jobOwner.UserProfileIdSales,
      DisplayCommissionRatePercentage: commissionRateResult?.ratePercentage,
      CommissionRatePercentage: commissionRateResult?.isCommissionRateProgram ? commissionRateResult.ratePercentage : null,
      IsOverridden: jobOwner.IsOverridden ?? false,
      OverrideReason: jobOwner.OverrideReason,
      OverrideByUserProfileId: jobOwner.OverrideByUserProfileId,
      OverrideByFullName: jobOwner.OverrideByFullName,
      OverrideDatetime: jobOwner.OverrideDatetime,
      MarginSplitPercentage: jobOwner.MarginSplitPercentage
    }, { emitEvent: false });

    const formArray = formGroup.get('CommissionRates') as FormArray<ICommissionRate>;
    this.updateCommissionRateFormArray(formArray, jobOwner.CommissionRates, jobOwner.CommissionRateHeaderId);
  }

  private createCommissionRateFormGroup(rate: Partial<ICommissionRate>): FormGroup<ICommissionRate> {
    const formGroup = this.fb.group<Partial<ICommissionRate>>({});
    Object.keys(rate).forEach(key => formGroup.addControl(key, new FormControl(rate[key])));
    return formGroup as FormGroup<ICommissionRate>;
  }

  private createCommissionRateFormArray(rates: Array<Partial<ICommissionRate>> = [], commissionRateHeaderId?: number): FormArray<ICommissionRate> {
    if (rates.length === 1) {
      rates[0].CommissionRateHeaderId = commissionRateHeaderId;
    }
    return this.fb.array<ICommissionRate>(
      rates.map((rate: ICommissionRate) => this.createCommissionRateFormGroup(rate))
    );
  }

  private updateCommissionRateFormArray(formArray: FormArray<ICommissionRate>, items: Array<Partial<ICommissionRate>> = [], commissionRateHeaderId?: number) {
    if (items.length === 1) {
      items[0].CommissionRateHeaderId = commissionRateHeaderId;
    }

    if (formArray.length && items.length) {
      items.forEach((item, index) => {
        const formGroup = formArray.at(index) as FormGroup<ICommissionRate>;
        if (formGroup) {
          formGroup.patchValue(item, { emitEvent: false });
        } else {
          formArray.push(this.createCommissionRateFormGroup(item));
        }
      });
      if (formArray.length > items.length) {
        this.clearArray(formArray, items.length);
      }
    } else if (items.length) {
      const array = this.createCommissionRateFormArray(items, commissionRateHeaderId);
      array.controls.forEach(group => {
        formArray.push(group);
      });
    } else {
      this.clearArray(formArray);
    }
  }

  private clearArray(formArray: FormArray<IJobOwner | ICommissionRate>, count = 0) {
    while (formArray.length !== count && count < formArray.length) {
      formArray.removeAt(count);
    }
  }

  private maxTotalValidator(property: string, max: number): ValidatorFn {
    return (formArray: AbstractControl): ValidationErrors | null => {
      const total = formArray.value.reduce((acc: number, item: any) => acc + (item[property] ? Number(item[property]) : 0), 0);
      return total > max ? { maxMarginSplitPercentageTotal: { max, actual: total } } : null;
    };
  }

  getCommissionRatePercentage(commissionRateHeaderId: number, isOverridden: boolean, commissionRatePercentage: number, commissionRates: ICommissionRate[])
    : { isCommissionRateProgram: boolean, ratePercentage: number }
  {
    if (commissionRateHeaderId) {
      if (isOverridden === true) {
        return {
          isCommissionRateProgram: true,
          ratePercentage: commissionRatePercentage
        };
      }

      const effectiveCommissionRateVersion = commissionRates
        .find(x => x.CommissionRateHeaderId === commissionRateHeaderId)
        .CommissionRateVersions
        .filter(x => new Date(x.EffectiveDate) <= new Date(this.workOrder.WorkOrderVersion.EffectiveDate))[0];
      
      if (effectiveCommissionRateVersion?.CommissionRateProgramId && effectiveCommissionRateVersion?.CommissionRateProgramTiers.length) {
        const amountRate = this.getCommissionRateProgramTierRate(effectiveCommissionRateVersion.CommissionRateProgramTiers,
          PhxConstants.CommissionRateType.FixedAmount, this.workOrderGrossMarginService.grossMarginDetails.GrossMargin);

        const percentageRate = this.getCommissionRateProgramTierRate(effectiveCommissionRateVersion.CommissionRateProgramTiers,
          PhxConstants.CommissionRateType.FixedPercentage, this.workOrderGrossMarginService.grossMarginDetails.GrossMarginPercentage);

        return {
          isCommissionRateProgram: true,
          ratePercentage: amountRate > percentageRate ? amountRate : percentageRate
        };
      }

      return {
        isCommissionRateProgram: false,
        ratePercentage: effectiveCommissionRateVersion?.Percentage
      };
    }
    
    return null;
  }

  private getCommissionRateProgramTierRate(tiersJson: string, commissionRateType: number, amount: number): number {
    const tiers = JSON.parse(tiersJson);
    return tiers.find((t: TierDto) => t.CommissionRateType === commissionRateType && t.Min <= amount && (!t.Max || t.Max >= amount))?.Rate ?? 0;
  }

  private setShowCommissionRateColumn(commissions: IJobOwner[]) {
    // CommissionRates will only have CommissionRateVersions at this point if the user has access
    this.showCommissionRateColumn = commissions.some(x => x.CommissionRates.some(r => r.CommissionRateVersions.length));
  }

}
